import { useEffect, useRef } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

/**
 * Modal de confirmação reutilizável, no tom "Conto Dourado".
 * Controlado pelo pai: `aberto` + `onConfirmar`/`onCancelar`.
 * Esc e tocar fora cancelam; o foco vai para o botão de confirmar.
 */
export function Confirmacao({
  aberto,
  titulo = "Tens a certeza?",
  mensagem,
  textoConfirmar = "Confirmar",
  textoCancelar = "Cancelar",
  destrutivo = false,
  aProcessar = false,
  onConfirmar,
  onCancelar,
}: {
  aberto: boolean;
  titulo?: string;
  mensagem: string;
  textoConfirmar?: string;
  textoCancelar?: string;
  /** Estilo de aviso (vermelho/blush) para ações destrutivas. */
  destrutivo?: boolean;
  /** Mostra estado "a processar…" e desativa os botões. */
  aProcessar?: boolean;
  onConfirmar: () => void;
  onCancelar: () => void;
}) {
  const reduzir = useReducedMotion();
  const refConfirmar = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!aberto) return;
    refConfirmar.current?.focus();
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === "Escape" && !aProcessar) onCancelar();
    }
    window.addEventListener("keydown", aoTeclar);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", aoTeclar);
      document.body.style.overflow = "";
    };
  }, [aberto, aProcessar, onCancelar]);

  return (
    <AnimatePresence>
      {aberto && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={() => !aProcessar && onCancelar()}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-tinta/60 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label={titulo}
        >
          <motion.div
            initial={reduzir ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduzir ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-3xl border border-ouro/20 bg-creme p-6 text-center shadow-2xl"
          >
            <h2 className="font-display text-2xl text-tinta">{titulo}</h2>
            <p className="mt-3 text-sm leading-relaxed text-tinta-700">{mensagem}</p>

            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={onCancelar}
                disabled={aProcessar}
                className="rounded-full px-5 py-2 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200 disabled:opacity-60"
              >
                {textoCancelar}
              </button>
              <button
                ref={refConfirmar}
                type="button"
                onClick={onConfirmar}
                disabled={aProcessar}
                className={`rounded-full px-6 py-2 text-sm font-semibold text-creme-50 shadow-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                  destrutivo
                    ? "bg-blush text-tinta hover:bg-blush-300"
                    : "bg-ouro hover:bg-ouro-600"
                }`}
              >
                {aProcessar ? "A processar…" : textoConfirmar}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
